import React, { useMemo } from "react";
import { topicDictionary } from "../../Util/tileDictionary";
import ComparisonTableRow from "./ComparisonTableRow";
import ENV from '../../Util/env';

// Special data-value codes where values should be suppressed
function isSpecialDataCode(v) {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  return s === "111111" || s === "333333" || s === "555555";
}

// Helper function to check if value is 0 or 100
function isZeroOrHundred(v) {
  if (v === null || v === undefined) return false;
  const n = Number(v);
  return !isNaN(n) && (n === 0 || n === 100);
}

// Helper function to check if data contains specific suppression codes
function hasSuppressionCode(data, code) {
  if (!data?.Rows || !Array.isArray(data.Rows)) return false;
  
  const checkValue = (v) => {
    if (v === null || v === undefined) return false;
    return String(v).trim() === code;
  };
  
  return data.Rows.some(row => 
    checkValue(row?.YearA?.DataValue) ||
    checkValue(row?.YearA?.DataValueRaw) ||
    checkValue(row?.YearA?.Percent) ||
    checkValue(row?.YearB?.DataValue) ||
    checkValue(row?.YearB?.DataValueRaw) ||
    checkValue(row?.YearB?.Percent)
  );
}

export default function ComparisonTable({ data, question, showSampleSize, title = "State Surveys", variant = "view1", onSubQuestionChange, questionId, onLocationChange }) {
  const topicColor = topicDictionary[question.TopicId].color;
  // Order years and labels: primary selected Year (A) first, then comparison Year (B)
  const years = useMemo(() => [data?.Meta?.Year, data?.Meta?.CompareYear], [data]);
  const labels = useMemo(() => [data?.Meta?.LabelA, data?.Meta?.LabelB], [data]);
  // Stable row keys to avoid React key warnings
  const getRowKey = (r, i) => {
    if (!r) return `row-${i}`;
    if (variant === "view1") {
      const abbr = r?.Location?.Abbr || r?.Location?.Desc;
      const type = r?.Location?.Type;
      if (abbr || type) return `loc-${abbr || ""}-${type || ""}`;
    }
    if (variant === "view2") {
      if (r?.SubQuestionId != null) return `sq-${r.SubQuestionId}`;
      if (r?.Label) return `label-${r.Label}`;
    }
    if (variant === "view3") {
      const y = r?.DisplayYear || r?.Year || r?.Yr || r?.YearA?.Year || r?.YearA?.Yr || r?.YearB?.Year || r?.YearB?.Yr;
      if (y != null) return `year-${y}`;
    }
    // Fallback to composite hash or index
    return `row-${i}`;
  };
  // Rows behavior:
  // - view1 (ALL locations): National first, then States alpha (dedup)
  // - view2 (specific location): Use rows as-is (typically sub-questions)
  const rows = useMemo(() => {
    const arr = Array.isArray(data?.Rows) ? data.Rows : [];
    if (variant === "view2") return arr;
    if (variant === "view3") {
      // For view3 (location-only compare on a specific subquestion), rows represent years.
      // Normalize to ensure both Meta years are present and ordered, using any rows the API returned.
      const numeric = (v) => {
        const n = Number((v ?? "").toString().replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? n : undefined;
      };
      const metaYears = [data?.Meta?.Year, data?.Meta?.CompareYear]
        .map(numeric)
        .filter((v) => v !== undefined);
      // Be resilient to API field naming: Year vs Yr, and optional flat Year fields
      const getRowYear = (r) =>
        numeric(
          r?.YearA?.Year ?? r?.YearA?.Yr ??
          r?.YearB?.Year ?? r?.YearB?.Yr ??
          r?.Year ?? r?.Yr ?? r?.Label
        );
      const rowYears = (arr || [])
        .map((r) => getRowYear(r))
        .filter((v) => v !== undefined);
      const allYears = Array.from(new Set([...rowYears, ...metaYears])).sort((a, b) => a - b);
      if (!allYears.length) {
        // Fallback: still attach a DisplayYear when we can infer it
        return arr.map((r) => ({ ...r, DisplayYear: getRowYear(r) }));
      }
      // For each desired year, pick the first matching row
      const pickForYear = (y) =>
        arr.find((r) => {
          const yA = numeric(r?.YearA?.Year ?? r?.YearA?.Yr);
          const yB = numeric(r?.YearB?.Year ?? r?.YearB?.Yr);
          const flat = numeric(r?.Year ?? r?.Yr);
          const lbl = numeric(r?.Label);
          return y === yA || y === yB || y === flat || y === lbl;
        });
      const normalized = allYears
        .map((y) => {
          const match = pickForYear(y);
          return match ? { ...match, DisplayYear: y } : null;
        })
        .filter(Boolean);
      // If normalization collapsed to zero/one rows (e.g., API returned a single year), fall back to original
      return normalized.length >= 2
        ? normalized
        : (normalized.length
          ? normalized
          : arr.map((r) => ({ ...r, DisplayYear: getRowYear(r) })));
    }
    // Default: view1 grouping and sort
    const seen = new Set();
    const uniq = arr.filter((r) => {
      const abbr = (r?.Location?.Abbr || r?.Location?.Desc || "").toUpperCase();
      const type = (r?.Location?.Type || "").toUpperCase();
      const key = `${abbr}|${type}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const nat = uniq.filter((r) => r?.Location?.Type === "National");
    const states = uniq
      .filter((r) => r?.Location?.Type === "State")
      .sort((a, b) => (a.Location?.Desc || "").localeCompare(b.Location?.Desc || ""));
    const others = uniq.filter(
      (r) => r?.Location?.Type !== "National" && r?.Location?.Type !== "State"
    );
    return [...nat, ...states, ...others];
  }, [data, variant]);

  const showPValue = ENV.ShowPValueColumn;

  return (
    <div className="table-responsive" role="region" aria-labelledby="cmp-caption">
      <table
        className="table table-striped table-sm comparison-table"
        style={{ borderCollapse: "collapse", "--cmp-topic-color": topicColor }}
        role="table"
      >
        <caption id="cmp-caption"></caption>
        <thead>
          {/* Row 1: no row background or borders; cells keep blue background */}
          <tr className="comparison-table__head-row comparison-table__head-row--groups">
            <th className="comparison-table__title">{title}</th>
            <th
              className="th comparison-table__head-year-a"
              colSpan={showSampleSize ? 3 : 2}
            >
              {variant === "view3"
                ? (labels[0] || "")
                : (labels[0] ? `${labels[0]} ${years[0]}` : years[0])}
            </th>
            <th
              className="th comparison-table__head-year-b"
              colSpan={showSampleSize ? 3 : 2}
            >
              {variant === "view3"
                ? (labels[1] || "")
                : (labels[1] ? `${labels[1]} ${years[1]}` : years[1])}
            </th>
            {showPValue && (
              <th className="th comparison-table__head-pvalue">p-value</th>
            )}
            <th className="th comparison-table__head-more-a">{variant === "view3" ? (labels[0] || "") : (labels[0] ? `${labels[0]} ${years[0]}` : years[0])} more likely<br /> than {variant === "view3" ? (labels[1] || "") : (labels[1] ? `${labels[1]} ${years[1]}` : years[1])}</th>
            <th className="th comparison-table__head-more-b">{variant === "view3" ? (labels[1] || "") : (labels[1] ? `${labels[1]} ${years[1]}` : years[1])} more likely<br /> than {variant === "view3" ? (labels[0] || "") : (labels[0] ? `${labels[0]} ${years[0]}` : years[0])}</th>
            <th className="th comparison-table__head-nodiff">No difference</th>
          </tr>
          {/* Row 2: light gray background (#0000000d) with borders */}
          <tr className="comparison-table__head-row comparison-table__head-row--labels">
            <th className="comparison-table__label-title-empty"></th>
            {showSampleSize && (<th className="comparison-table__label-a-n">N</th>)}
            <th className="comparison-table__label-a-pct">%</th>
            <th className="comparison-table__label-a-ci">CI</th>
            {showSampleSize && (<th className="comparison-table__label-b-n">N</th>)}
            <th className="comparison-table__label-b-pct">%</th>
            <th className="comparison-table__label-b-ci">CI</th>
            {showPValue && (
              <th className="comparison-table__label-pvalue"></th>
            )}
            <th className="comparison-table__label-more-a text-center"></th>
            <th className="comparison-table__label-more-b text-center"></th>
            <th className="comparison-table__label-nodiff text-center"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <ComparisonTableRow
              key={getRowKey(r, i)}
              row={r}
              years={years}
              showSampleSize={showSampleSize}
              onSubQuestionChange={onSubQuestionChange}
              questionId={questionId}
              onLocationChange={onLocationChange}
              variant={variant}
              topicColor={topicColor}
              showPValue={showPValue}
            />
          ))}

          {/* Range row computed from visible rows */}
          {(() => {
            // Compute range across like-for-like rows:
            // - For State Surveys: use only State rows (exclude National)
            // - For Local Surveys: use only Local rows
            const isLocalTable = rows.every((r) => r?.Location?.Type === "Local");
            const source = rows.filter((r) =>
              isLocalTable ? r?.Location?.Type === "Local" : r?.Location?.Type === "State"
            );
            const toNums = (vals) => vals
              .filter((v) => v != null && !isSpecialDataCode(v) && !isZeroOrHundred(v) && !Number.isNaN(Number(v)))
              .map(Number);
            const valsA = toNums(source.map((r) => r?.YearA?.Percent));
            const valsB = toNums(source.map((r) => r?.YearB?.Percent));
            const fmt = (low, high) => {
              if (low == null || high == null) return "—";
              // Format 0 and 100 without decimals, others with 1 decimal
              const fmtNum = (n) => (n === 0 || n === 100) ? String(Math.round(n)) : n.toFixed(1);
              return `${fmtNum(low)} - ${fmtNum(high)}`;
            };
            const rangeA = valsA.length ? fmt(Math.min(...valsA), Math.max(...valsA)) : "—";
            const rangeB = valsB.length ? fmt(Math.min(...valsB), Math.max(...valsB)) : "—";
            if (valsA.length + valsB.length === 0) return null;
            return (
              <tr className="comparison-table__range-row">
                <td style={{ textAlign: "left", borderRight: "none" }}><b>Range</b></td>
                {/* Year A group */}
                {showSampleSize && <td></td>}
                <td><strong>{rangeA}</strong></td>
                <td></td>
                {/* Year B group */}
                {showSampleSize && <td></td>}
                <td><strong>{rangeB}</strong></td>
                <td></td>
                {/* Only render p-value column if visible */}
                {showPValue && <td></td>}
                {/* Comparison columns placeholders */}
                <td></td>
                <td></td>
                <td></td>
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
}
